<script setup>
import { useUserStore } from "@/stores/UserStore"
import { onMounted } from "vue"

const props = defineProps({
  completedSteps: {
    type: Object,
    required: true,
  },
})

const userStore = useUserStore()

const stepsMeter = ref(0)
const expandedOnBoarding = ref(true)
const steps = ref(null)

const docsURL = import.meta.env.VITE_DOCS_URL

const optionalSteps = [
  {
    'id': 'discounts_created',
    'title': 'Add Shopthru exclusive discounts',
    'subtitle': 'Attract customers with special offers and boost your sales',
    'action': 'discount/retailer/list',
    'docs': 'selling-profile/discounts',
  },
]

const stepsUpdate = () => {
  if (steps && props.completedSteps?.length) {
    steps?.value?.forEach(step => {
      step.is_completed = props.completedSteps.includes(step.id)
    })
  }

  completedPercentage()
}

const completedPercentage = () => {
  const totalSteps = steps.value?.length
  const completedSteps = steps.value?.filter(step => step.is_completed).length

  stepsMeter.value = totalSteps > 0 ? ((completedSteps / totalSteps) * 100).toFixed(2) : 0
}

function parsePath(fullPath) {
  const [path, queryString] = fullPath.split('?')
  const params = {}

  if (!fullPath) return '/'

  if (queryString) {
    const queryParams = queryString.split('&')

    queryParams.forEach(param => {
      const [key, value] = param.split('=')
      if (key) {
        params[decodeURIComponent(key)] = decodeURIComponent(value || '')
      }
    })
  }

  return {
    name: path.replace(/\//g, '-'),
    query: params,
  }
}

onMounted(async () => {
  steps.value = await userStore.getOnboardingSteps()

  stepsUpdate()
})

watch(props, () => {
  stepsUpdate()
})
</script>

<template v-if="steps">
  <div
    id="onboarding-slide"
    class="d-flex align-end"
  >
    <VBtn
      id="onboarding-toggle"
      variant="flat"
      color="primary"
      @click="expandedOnBoarding = true"
    >
      Open Onboarding
    </VBtn>
    <VFadeTransition>
      <VCard
        v-show="expandedOnBoarding"
        id="onboarding-card"
        title="Complete Onboarding"
        max-width="300"
      >
        <template #append>
          <IconBtn
            color="primary"
            variant="tonal"
            @click="expandedOnBoarding = false"
          >
            <VIcon
              size="26"
              icon="tabler-arrow-bar-to-right"
            />
          </IconBtn>
        </template>

        <VCardText>
          <VProgressLinear
            v-model="stepsMeter"
            height="20"
            color="primary"
            class="mb-6"
          >
            <span class="text-light-grey font-weight-bold">{{ Math.round(stepsMeter) }}%</span>
          </VProgressLinear>

          <VList
            v-if="steps?.length"
            class="card-list"
            :lines="false"
          >
            <VListItem
              v-for="step in steps"
              :key="step"
              :active="false"
              :disabled="step.is_completed"
              class="border-b-sm mb-3"
            >
              <template #prepend>
                <VAvatar
                  v-if="step.is_completed"
                  size="34"
                  color="success"
                  variant="flat"
                >
                  <VIcon icon="tabler-check" />
                </VAvatar>
                <VHover v-else>
                  <template #default="{ isHovering, props }">
                    <RouterLink
                      :to="parsePath(step.action)"
                      class="d-block mr-4"
                    >
                      <VAvatar
                        v-bind="props"
                        size="34"
                        color="primary"
                        :variant="isHovering ? 'flat' : 'tonal'"
                      ><VIcon icon="tabler-point" /></VAvatar>
                    </RouterLink>
                  </template>
                </VHover>
              </template>

              <VListItemTitle
                v-if="step.title"
                :class="!step.is_completed || 'text-decoration-line-through'"
                class="font-weight-medium text-capitalize whitespace-normal"
                style="cursor: default;"
              >
                {{ step.title }}
              </VListItemTitle>

              <VListItemSubtitle
                v-if="step.subtitle"
                :class="!step.is_completed || 'text-decoration-line-through'"
                style="cursor: default;"
              >
                {{ step.subtitle }}
                <a
                  v-if="step.docs && docsURL"
                  class="d-flex align-center mt-2 cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                  :href="docsURL + step.docs"
                >See documentation
                  <VIcon
                    size="16"
                    icon="tabler-external-link"
                    class="ml-1"
                  />
                </a>
              </VListItemSubtitle>
            </VListItem>
            <template v-if="optionalSteps?.length">
              <span class="text-h6 d-block mb-6">Why not look into some optional steps while you wait to be authorised?</span>

              <VListItem
                v-for="step in optionalSteps"
                :key="step"
                :active="false"
                class="border-b-sm mb-3"
              >
                <template #prepend>
                  <VHover>
                    <template #default="{ isHovering, props }">
                      <RouterLink
                        :to="parsePath(step.action)"
                        class="d-block mr-4"
                      >
                        <VAvatar
                          v-bind="props"
                          size="34"
                          color="secondary"
                          :variant="isHovering ? 'flat' : 'tonal'"
                        >
                          <VIcon icon="tabler-minus" />
                        </VAvatar>
                      </RouterLink>
                    </template>
                  </VHover>
                </template>

                <VListItemTitle
                  v-if="step.title"
                  :class="!step.is_completed || 'text-decoration-line-through'"
                  class="font-weight-medium text-capitalize whitespace-normal cursor-auto"
                >
                  {{ step.title }}
                </VListItemTitle>

                <VListItemSubtitle
                  v-if="step.subtitle"
                  :class="!step.is_completed || 'text-decoration-line-through'"
                >
                  {{ step.subtitle }}
                  <a
                    v-if="step.docs && docsURL"
                    class="d-flex align-center mt-2 cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                    :href="docsURL + step.docs"
                  >See documentation
                    <VIcon
                      size="16"
                      icon="tabler-external-link"
                      class="ml-1"
                    />
                  </a>
                </VListItemSubtitle>
              </VListItem>
            </template>
          </VList>
        </VCardText>
      </VCard>
    </VFadeTransition>
  </div>
</template>

<style lang="scss">
  #onboarding-slide {
    position: fixed !important;

    // To keep button on top of v-layout. E.g. Email app
    z-index: 999;
    inset-block-end: 120px;
    inset-inline-end: 25px;
    right: 0;
    max-height: 350px;
    height: 100%;
  }

 #onboarding-toggle {
   position: fixed;
   right: 0;
   transform-origin: bottom right;
   transform: rotate(-90deg);
   bottom: 300px;
   box-shadow: -2px -4px 6px -1px rgb(var(--v-theme-primary), 0.5);
 }

 #onboarding-card {
   box-shadow: -2px 0 10px 0 rgb(var(--v-theme-primary), 0.5);
   overflow-y: auto;
   height: 100%;
 }
 .v-list-item:last-of-type {
   border-bottom: none !important;
 }
</style>
